import React, { useCallback } from 'react';
import { FeedbackSingleFilterProps } from './FeedbackFilterDropdown';
import FeedbackDashboardFilterSelect from '../FeedbackDashboardFilterSelect';
import FormGroup from '@mui/material/FormGroup/FormGroup';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import TrueImpactDropdownRow from '../../../design-system/components/TrueImpactDropdownRow';

const getSelectorValues = <T extends string = string>(
  feedbackFilter: FeedbackSingleFilterProps<T>,
  defaultValue: T,
): { label: T; isAnySelected: boolean } => {
  const findSelected = Object.values(feedbackFilter.filters).find(
    x => x.selected,
  );
  return {
    label: (findSelected?.name as T) || defaultValue,
    isAnySelected: !!findSelected,
  };
};

interface FeedbackSingleSelectFilterProps<T extends string = string> {
  defaultValue: T;
  feedbackFilter: FeedbackSingleFilterProps<T>;
  icon: IconProp | IconDefinition;
  showClearAll: boolean;
  className?: string;
}

const FeedbackSingleSelectFilter = <T extends string = string>({
  feedbackFilter,
  defaultValue,
  showClearAll,
  icon,
  className,
}: FeedbackSingleSelectFilterProps<T>) => {
  const handleClick = useCallback(
    (key: T) => {
      feedbackFilter.applyFilter(key);
    },
    [feedbackFilter],
  );

  const Component = (handleClose: () => unknown) => (
    <FormGroup>
      {Object.keys(feedbackFilter.filters).map(d => {
        const f = feedbackFilter.filters[d];
        return (
          <TrueImpactDropdownRow
            key={d}
            selected={f.selected}
            text={f.name}
            onClick={() => {
              handleClose();
              handleClick(d as T);
            }}
          />
        );
      })}
    </FormGroup>
  );

  const { label, isAnySelected } = getSelectorValues(
    feedbackFilter,
    defaultValue,
  );
  return (
    <FeedbackDashboardFilterSelect
      type="radio"
      disableClearButton={!isAnySelected}
      clearButtonText="Clear"
      selectorLabel={label}
      icon={icon}
      onClearAll={showClearAll ? feedbackFilter.onClear : undefined}
      onClose={undefined}
      className={className}
      Component={Component}
    />
  );
};

export default FeedbackSingleSelectFilter;
